// The Clients list — THE PORTFOLIO BOARD ('#clients').
//
// This is the only surface in Signet on which the whole portfolio exists at once. The operator
// arrives with one question — "across everything I am carrying, what needs a move, and is
// anything going quiet on me?" — and must leave in one of exactly two states: inside one
// client's screen WITH A STATED REASON for being there, or certain that nothing is waiting.
//
//   masthead   the page name, the register's three facts, one quiet verb
//   rail       five cells over the WHOLE register — ENGAGED · QUEUED · BALL · QUIET · NEW
//   toolbar    exhaustive status chips with their own counts, and the search
//   zone 1     NEEDS A MOVE      ranked by the rule that fired, lead card + rows
//   zone 2     NOTHING QUEUED    no rule fired; the gate tally and the silence, read here
//   zone 3     NOT ENGAGED       folded; the two disposals live here and nowhere else
//
// FIVE LOAD-BEARING RULES, from the study:
//
//   1. One client, exactly one zone. `zone` is computed server-side and is total.
//   2. The scope rule, one level up: anything derived from the SET renders in the rail; anything
//      from one client_id renders in that client's row; anything from an engagement_id renders
//      inside that engagement's chip on that row. Never a page-level phase or gate.
//   3. The rail is invariant — five cells, fixed order, rendered in every state. Nothing to say
//      is '—'; the roll-up failing says so on all five rather than showing a stale number.
//   4. The board never calls /brief. It READS client_visits; browsing does not burn the NEW
//      markers the briefs depend on.
//   5. The board and the brief share one code path. Every move title, `why`, weight and route is
//      `buildQueue`'s own string, rendered UNEDITED (B7).
//
// What the ordering does: `ORDER BY name` handed the first-click decision to the alphabet
// (Krug p.42 — we satisfice, we click the first plausible row). The sort key here is the weight
// of the rule that fired, then the silence, and `name` only as the last tie-break.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useToast } from '../components/Toast.js';
import { useEngagement } from '../context/EngagementContext.js';
import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState, { ProblemState } from '../components/EmptyState.js';
import { RailCell, RowMenu, type RailTone } from '../components/PageKit.js';
import { usePoll } from '../hooks/usePoll.js';
import {
  ROUTES, resolveRouteRef, useDeepLink, useOpenRoute, useRouteParams, type Dest,
} from '../routes.js';
import { boardApi, BOARD_RAIL_KEYS, type BoardClient, type BoardPayload, type BoardQueueItem, type BoardRailCell, type BoardRoute } from '../api/clientsBoard.js';
import * as crm from '../api/crm.js';
import type { Phase } from '../api/crm.js';
import { useLocale, useT } from '../i18n/index.js';
import './ClientsPage.de.js';
import './ClientsPage.fr.js';

/** `t()` for a sentence with markup inside: each `{name}` placeholder is filled with a React node. */
function tx(t: (key: string) => string, key: string, nodes: Record<string, ReactNode>): ReactNode {
  return t(key).split(/(\{\w+\})/g).map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    return m ? <Fragment key={i}>{nodes[m[1]]}</Fragment> : part;
  });
}

/** The 'close' PHASE label collides with the fleet-wide verb key 'Close' ('Schliessen') — route it
 *  through its own key 'Close (phase)' ('Abschluss'). English keeps the plain word: a missing
 *  dictionary returns the key itself, so we fall back to 'Close' by hand. */
function tPhase(t: (key: string) => string, label: string): string {
  if (label !== 'Close') return t(label);
  const s = t('Close (phase)');
  return s === 'Close (phase)' ? 'Close' : s;
}

/** The board is a triage surface, not a live console: 20s is the study's cadence. */
const POLL_MS = 20_000;

/** Exhaustive over `clients_status_check` — a status the schema admits must be selectable (L9). */
const CLIENT_STATUSES: Array<crm.Client['status']> = ['active', 'dormant', 'archived'];

/** Where a resolved destination sends you, in the operator's words. */
const PAGE_LABEL: Record<string, string> = {
  dashboard: 'the dashboard',
  clients: 'the clients',
  lifecycle: 'the gate',
  documents: 'the documents',
  register: 'the register',
  agent: 'the agent queue',
  proposals: 'the proposals',
  contracting: 'contracting',
  acceptance: 'acceptance',
  'portal-admin': 'the client portal',
  admin: 'admin',
};

export default function ClientsPage() {
  const toast = useToast();
  const { t, tp, fmtDate } = useLocale();
  const nav = useEngagement();
  const openRoute = useOpenRoute(nav);
  const params = useRouteParams();

  // One payload, polled. Numbers are never blanked on a failed refresh (F5.3).
  const load = useCallback(() => boardApi.get(), []);
  const { data: board, loading, error, notice, refresh } = usePoll<BoardPayload>(load, POLL_MS);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [foldOpen, setFoldOpen] = useState(false);
  /** The anchor a rail cell asked for; applied once the row it names exists. */
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);

  /** The degraded mode the error state offers: names and identities, no roll-up. */
  const [plainRows, setPlainRows] = useState<crm.Client[] | null>(null);

  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClient, setNewClient] = useState<Partial<crm.Client>>({ name: '', status: 'active' });

  const [confirm, setConfirm] = useState<
    | { kind: 'archive-client'; id: number; label: string }
    | { kind: 'delete-client'; id: number; label: string; plan: crm.ClientDeletionPlan }
    | null
  >(null);
  const [busy, setBusy] = useState(false);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Arrival intents. This board declares none of its own — `anchor` is handled for us, so a
  // cell on another tab can route to '#clients?anchor=zone-quiet' and land on the zone.
  useDeepLink(params, {});

  // ── the filtered view ─────────────────────────────────────────────────────────
  // Page-local and gone on unmount (B4). The rail and the chip counts read the WHOLE register:
  // a cell that counted only what survived the filter would be counting the filter.

  const all = useMemo(() => board?.clients ?? [], [board]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return all.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!needle) return true;
      // The same three columns `GET /api/clients?q=` matches on.
      return [c.name, c.short_name, c.sector]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [all, query, statusFilter]);

  const moves = useMemo(
    () => rows.filter((c) => c.zone === 'move').sort((a, b) =>
      (a.queue[0]?.weight ?? 0) - (b.queue[0]?.weight ?? 0)
      || (b.window.days_since ?? 0) - (a.window.days_since ?? 0)
      || a.name.localeCompare(b.name)),
    [rows],
  );
  const quiets = useMemo(
    () => rows.filter((c) => c.zone === 'quiet').sort((a, b) =>
      (b.window.days_since ?? 0) - (a.window.days_since ?? 0) || a.name.localeCompare(b.name)),
    [rows],
  );
  const darks = useMemo(
    () => rows.filter((c) => c.zone === 'dark').sort((a, b) =>
      b.new_items - a.new_items || a.name.localeCompare(b.name)),
    [rows],
  );

  /**
   * ATTEST §5, with the Contracting precedent: exactly one filled control exists in every state,
   * and it is always the one the data says to press. A queued move outranks "New client" — the
   * audit log says the header verb was the wrong one (21 creates, two of them real).
   */
  const headerPrimary = moves.length === 0;

  // ── routing ───────────────────────────────────────────────────────────────────

  const openClient = useCallback(
    (id: number, anchor?: string | null) => void openRoute(ROUTES.client({ id, anchor: anchor ?? null })),
    [openRoute],
  );

  /** A move's route, resolved once. Server-emitted refs are never composed into hashes here. */
  const openMove = useCallback((client: BoardClient, route: BoardRoute) => {
    const r = resolveRouteRef(route as Record<string, unknown> | null);
    if (r.kind === 'dest') { void openRoute(r.dest); return; }
    // Both of these name a surface that lives on the CLIENT page — the anchor travels with it.
    if (r.kind === 'in-page') { openClient(client.id, r.anchor); return; }
    if (r.kind === 'client-view') { openClient(client.id, r.anchor); return; }
    toast('warn', r.reason);
  }, [openRoute, openClient, toast]);

  /** The label the move's own route earns. Never invented from the title. */
  const moveLabel = useCallback((route: BoardRoute): string => {
    const r = resolveRouteRef(route as Record<string, unknown> | null);
    if (r.kind === 'dest') return t('Open {page} →', { page: t(PAGE_LABEL[(r.dest as Dest).page] ?? 'the page') });
    if (r.kind === 'client-view') return r.view === 'timeline' ? t('The timeline →') : t('Open the client →');
    if (r.kind === 'in-page') return t('Open the client →');
    return t('The brief →');
  }, [t]);

  /** A rail cell routes in-page. When it names a row, the row may be folded away or filtered out. */
  const onRail = useCallback((cell: BoardRailCell) => {
    const r = resolveRouteRef(cell.route as Record<string, unknown> | null);
    if (r.kind === 'none') { toast('warn', r.reason); return; }
    if (r.kind !== 'in-page') return;
    const id = r.anchor.startsWith('#') ? r.anchor.slice(1) : r.anchor;
    const named = /^client-row-(\d+)$/.exec(id);
    if (named) {
      const target = all.find((c) => c.id === Number(named[1]));
      if (target?.zone === 'dark') setFoldOpen(true);
    }
    setPendingAnchor(id);
  }, [all, toast]);

  useEffect(() => {
    if (!pendingAnchor) return;
    const land = (): boolean => {
      const node = document.getElementById(pendingAnchor);
      if (!node) return false;
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };
    if (land()) { setPendingAnchor(null); return; }
    // One beat for a band that has just been unfolded, then say so rather than doing nothing.
    const timer = setTimeout(() => {
      if (!land()) {
        toast('warn', t('That row is not on the board under the current filter — the cell counts the whole register.'));
      }
      setPendingAnchor(null);
    }, 80);
    return () => clearTimeout(timer);
  }, [pendingAnchor, foldOpen, toast, t]);

  // ── mutations ─────────────────────────────────────────────────────────────────

  async function createClient() {
    if (!newClient.name?.trim()) { toast('warn', t('Name is required')); return; }
    try {
      const created = await crm.createClient(newClient);
      toast('ok', t('{name} added', { name: created.name }));
      setNewClientOpen(false);
      setNewClient({ name: '', status: 'active' });
      // Straight onto the new client's page — the next step is always to fill it in.
      openClient(created.id);
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  /** Fetch the plan, THEN open the dialogue — never open one that cannot answer "why not". */
  async function askDeleteClient(id: number, label: string) {
    try {
      setConfirm({ kind: 'delete-client', id, label, plan: await crm.clientDeletionPlan(id) });
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  async function runConfirm() {
    if (!confirm) return;
    // A blocked plan's confirm performs the ALTERNATIVE the blocker names, so the operator is
    // never left staring at a dialogue that only knows how to say no.
    const blocked = confirm.kind === 'delete-client' && !confirm.plan.allowed;
    setBusy(true);
    try {
      if (confirm.kind === 'archive-client' || blocked) {
        await crm.archiveClient(confirm.id);
        toast('ok', t('Client archived — it keeps its history and leaves the active list'));
      } else {
        const out = await crm.deleteClient(confirm.id);
        toast('ok', t('{label} deleted — {cascade} went with it', {
          label: confirm.label,
          cascade: out.cascade.map((c) => `${c.count} ${c.what}`).join(', ') || t('nothing else'),
        }));
      }
      setConfirm(null);
      await refresh();
    } catch (err) {
      toast('err', (err as Error).message);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  async function showPlainList() {
    try {
      setPlainRows(await crm.listClients());
    } catch (err) {
      toast('err', (err as Error).message);
    }
  }

  // ── the masthead and the rail — rendered in EVERY state ───────────────────────

  const railCells: BoardRailCell[] = board
    ? board.readout
    : BOARD_RAIL_KEYS.map((key) => ({
      key,
      value: '—',
      unit: null,
      detail: loading ? t('reading…') : t('roll-up did not answer'),
      tone: 'unknown' as const,
      route: null,
    }));

  const clearFilter = () => { setQuery(''); setStatusFilter(''); };

  const noMatchWhat = [
    query ? t('“{query}”', { query }) : null,
    statusFilter ? t('status {status}', { status: t(statusFilter) }) : null,
  ].filter(Boolean).join(` ${t('under')} `);

  return (
    <div className="page cl-board">
      <header className="mast">
        <div className="mast-top">
          <div className="mast-main">
            <h1>{t('Clients')}</h1>
            <div className="mast-sub">
              {board ? (
                <>
                  <span className="voice-figure">{board.on_file}</span><span>{t('on file')}</span>
                  <span className="sep">·</span>
                  <span className="voice-figure">{board.engaged}</span><span>{t('engaged')}</span>
                  <span className="sep">·</span>
                  <span>{tx(t, 'board read at {date}', { date: <span className="voice-figure">{fmtDate(board.today)}</span> })}</span>
                </>
              ) : (
                <span className="n-none">{loading ? t('reading the register…') : t('the register did not answer')}</span>
              )}
              {notice && <><span className="sep">·</span><span className="cl-notice">{notice}</span></>}
            </div>
          </div>
          <div className="mast-verbs">
            <button
              type="button"
              className={`btn${headerPrimary ? ' btn-accent' : ''}`}
              onClick={() => setNewClientOpen(true)}
            >
              {t('New client')}
            </button>
          </div>
        </div>
      </header>

      {/* The instrument: one sheet carrying the rail and the filter bar (kit .instr). */}
      <div className="instr">
        <div className="rail">
          {railCells.map((cell) => (
            <RailCell
              key={cell.key}
              label={t(cell.key)}
              go={cell.route ? '→' : undefined}
              value={cell.value}
              unit={cell.unit || undefined}
              tone={cell.tone as RailTone}
              detail={cell.detail}
              onClick={cell.route ? () => onRail(cell) : undefined}
              disabled={!cell.route}
              title={cell.detail}
            />
          ))}
        </div>

        <div className="instr-band cl-tools">
            <div className="cl-chips">
              <button
                type="button"
                className={`chip${statusFilter === '' ? ' active' : ''}`}
                onClick={() => setStatusFilter('')}
              >
                {t('All')}<span className="cl-cnt voice-figure">{all.length}</span>
              </button>
              {CLIENT_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`chip${statusFilter === s ? ' active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {t(s)}
                  <span className="cl-cnt voice-figure">{all.filter((c) => c.status === s).length}</span>
                </button>
              ))}
            </div>
            <div className="cl-search">
              <input
                placeholder={t('Search name, short name, sector…')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && <button type="button" className="clr" onClick={() => setQuery('')}>×</button>}
            </div>
          </div>
      </div>

      {/* A sub-source failed: state it once, loud, in the producer's own words. */}
      {board && board.problems.length > 0 && (
        <section className="zone">
          <ProblemState problems={board.problems} source="gates" zone={t('The gate tallies')} onRetry={() => void refresh()} />
        </section>
      )}
      {board?.scale_notice && (
        <section className="zone">
          <div className="cl-znote">{board.scale_notice}</div>
        </section>
      )}

      {/* ── the body ─────────────────────────────────────────────────────────── */}

      {!board && loading && <BoardSkeleton />}

      {!board && !loading && (
        <section className="zone">
          <EmptyState
            kind="error"
            title={t('The board did not answer')}
            detail={tx(t, '{endpoint} answered: “{error}”. The client register itself is fine — what failed is the roll-up that ranks it.', {
              endpoint: <span className="src-ref">GET /api/clients/board</span>,
              error: error ?? t('no response'),
            })}
            onRetry={() => void refresh()}
          >
            {!plainRows && (
              <button type="button" className="btn btn-sm" onClick={() => void showPlainList()}>
                {t('Show the plain list instead')}
              </button>
            )}
          </EmptyState>
          {plainRows && (
            <section className="zone" id="zone-plain">
              <div className="zone-head">
                <span className="u-label">{t('The plain list')}</span>
                <span className="zone-count voice-figure">{tp(plainRows.length, '{n} client', '{n} clients')}</span>
              </div>
              <div className="cl-znote">
                {t('Names and identities only — no ranking, no gate tallies, no silence. This is the degraded mode.')}
              </div>
              <div className="sheet sheet--clip cl-band">
                {plainRows.map((c) => (
                  <div className="cl-dead" key={c.id}>
                    <div>
                      <div className="cl-deadname">
                        {c.name}
                        {c.short_name && <span className="src-ref">{c.short_name}</span>}
                        {c.status !== 'active' && <span className="cl-statelabel">{t(c.status)}</span>}
                      </div>
                      <div className="cl-deadwhat">{c.sector ?? t('no sector on the record')}</div>
                    </div>
                    <div className="cl-deadverbs">
                      <button type="button" className="btn btn-sm" onClick={() => openClient(c.id)}>{t('Open →')}</button>
                      {c.status !== 'archived' && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          title={t('Archive — keeps every engagement, contact and document; the client leaves the active list')}
                          onClick={() => setConfirm({ kind: 'archive-client', id: c.id, label: c.name })}
                        >
                          {t('Archive')}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        title={t('Delete {name} — for a row that should never have existed', { name: c.name })}
                        onClick={() => void askDeleteClient(c.id, c.name)}
                      >
                        {t('Delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </section>
      )}

      {board && all.length === 0 && (
        <section className="zone">
          <EmptyState
            kind="empty"
            title={t('No clients yet')}
            detail={t('A client is the root of everything: engagements hang off it, and the document library belongs to it. Add the first one, then create an engagement against it.')}
          >
            <button type="button" className="btn btn-accent" onClick={() => setNewClientOpen(true)}>{t('New client')}</button>
          </EmptyState>
        </section>
      )}

      {board && all.length > 0 && rows.length === 0 && (
        <section className="zone">
          <EmptyState
            kind="no-match"
            title={t('Nothing matches {what}', { what: noMatchWhat })}
            detail={tx(t, all.length === 1
              ? '{n} client is on file. The filter, not the register, is empty.'
              : '{n} clients are on file. The filter, not the register, is empty.', {
              n: <span className="voice-figure">{all.length}</span>,
            })}
            onClear={clearFilter}
            clearLabel={t('Clear the filter')}
          />
        </section>
      )}

      {/* ── zone 1 · NEEDS A MOVE ── */}
      {moves.length > 0 && (
        <section className="zone" id="zone-move">
          <div className="zone-head">
            <span className="u-label">{t('Needs a move')}</span>
            <span className="zone-count voice-figure">
              {tp(moves.length, '{n} client', '{n} clients')} · {tp(moves.reduce((n, c) => n + c.queue.length, 0), '{n} move', '{n} moves')}
            </span>
          </div>
          <div className="cl-znote">
            {t('Ranked by the rule that fired, not by name. The rule and its wording are the client page’s own — this board calls the same code.')}
          </div>

          <LeadCard
            client={moves[0]}
            primary={!headerPrimary}
            label={moveLabel(moves[0].queue[0].route)}
            onMove={() => openMove(moves[0], moves[0].queue[0].route)}
            onOpen={() => openClient(moves[0].id)}
            onArchive={() => setConfirm({ kind: 'archive-client', id: moves[0].id, label: moves[0].name })}
            onDelete={() => void askDeleteClient(moves[0].id, moves[0].name)}
          />

          {moves.length > 1 && (
            <div className="sheet sheet--clip cl-band">
              {moves.slice(1).map((c) => (
                <MoveRow
                  key={c.id}
                  client={c}
                  label={moveLabel(c.queue[0].route)}
                  onMove={() => openMove(c, c.queue[0].route)}
                  onOpen={() => openClient(c.id)}
                  onArchive={() => setConfirm({ kind: 'archive-client', id: c.id, label: c.name })}
                  onDelete={() => void askDeleteClient(c.id, c.name)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── zone 2 · NOTHING QUEUED ── */}
      {quiets.length > 0 && (
        <section className="zone" id="zone-quiet">
          <div className="zone-head">
            <span className="u-label">{t('Nothing queued')}</span>
            <span className="zone-count voice-figure">{tp(quiets.length, '{n} client', '{n} clients')}</span>
          </div>
          <div className="cl-znote">
            {tx(t, 'No rule fired. A gate tally and a silence are not rules — read them here. Quiet is {days} days or more with nothing recorded.', {
              days: <span className="voice-figure">{board?.quiet_days ?? 21}</span>,
            })}
          </div>
          <div className="sheet sheet--clip cl-band">
            {quiets.map((c) => (
              <QuietRow
                key={c.id}
                client={c}
                onOpen={() => openClient(c.id)}
                onGate={(engagementId) => void openRoute(ROUTES.lifecycle({ engagement: engagementId }))}
                onArchive={() => setConfirm({ kind: 'archive-client', id: c.id, label: c.name })}
                onDelete={() => void askDeleteClient(c.id, c.name)}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── zone 3 · NOT ENGAGED — the only zone that carries a disposal ── */}
      {darks.length > 0 && (
        <section className="zone" id="zone-dark">
          <div className="zone-head">
            <span className="u-label">{t('Not engaged')}</span>
            <span className="zone-count voice-figure">
              {tp(darks.length, '{n} client', '{n} clients')}
              {darks.reduce((n, c) => n + c.new_items, 0) > 0
                ? ` · ${tp(darks.reduce((n, c) => n + c.new_items, 0), '{n} new item', '{n} new items')}`
                : ''}
            </span>
          </div>
          <div className="sheet sheet--clip cl-band">
            <button type="button" className="cl-fold" onClick={() => setFoldOpen((v) => !v)}>
              <span className="u-label">{foldOpen ? t('Hide these rows') : t('Show these rows')}</span>
              <span className="zone-count">
                {t('No live engagement, so no gate and no queue beyond “create one”.')}
              </span>
              <span className="cl-foldgo">{foldOpen ? t('hide ▴') : t('show ▾')}</span>
            </button>
            {foldOpen && darks.map((c) => (
              <DeadRow
                key={c.id}
                client={c}
                onOpen={() => openClient(c.id)}
                onArchive={() => setConfirm({ kind: 'archive-client', id: c.id, label: c.name })}
                onDelete={() => void askDeleteClient(c.id, c.name)}
              />
            ))}
          </div>
          {foldOpen && (
            <div className="cl-znote" style={{ marginTop: 'var(--s8)' }}>
              {tx(t, 'NEW counts from your last visit; where a client has never been opened, from {days} days back.', {
                days: <span className="voice-figure">{board?.first_visit_window_days ?? 14}</span>,
              })}
            </div>
          )}
        </section>
      )}

      {/* ── New client ── */}
      {newClientOpen && (
        <div className="modal-overlay" onClick={() => setNewClientOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('New client')}</h2>
              <button className="close-btn" onClick={() => setNewClientOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* A stated omission is a decision; a silent one is a defect (RESEARCH §4, task D). */}
              <p className="text-sm text-ink-2" style={{ marginTop: 0, lineHeight: 1.6 }}>
                {tx(t, 'Name, short name and sector are what the list scans. Legal name and address are what the SOW prints — the record surface takes them, and the brief will show {dash} until it does.', {
                  dash: <span className="n-none">—</span>,
                })}
              </p>
              <div className="form-group">
                <label className="form-label">{t('Name')}</label>
                <input
                  className="form-input"
                  value={newClient.name ?? ''}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                />
              </div>
              <div className="flex gap-12">
                <div className="form-group" style={{ width: 160 }}>
                  <label className="form-label">{t('Short name')}</label>
                  <input
                    className="form-input mono"
                    placeholder="AVANTI"
                    value={newClient.short_name ?? ''}
                    onChange={(e) => setNewClient({ ...newClient, short_name: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">{t('Sector')}</label>
                  <input
                    className="form-input"
                    value={newClient.sector ?? ''}
                    onChange={(e) => setNewClient({ ...newClient, sector: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Notes')}</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={newClient.notes ?? ''}
                  onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                />
              </div>
              <div className="text-xs text-ink-3">
                {tx(t, 'The short name seeds derived engagement codes (e.g. {example}).', { example: <span className="mono">AVANTI01</span> })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setNewClientOpen(false)}>{t('Cancel')}</button>
              <button className="btn btn-accent" onClick={() => void createClient()}>{t('Create client')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── the two disposals — the plan is fetched before the dialogue opens, and the
             server recomputes it before acting: the plan the UI showed is a display,
             never an authority. ── */}
      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.kind === 'archive-client' ? t('Archive client')
            : confirm?.plan.allowed === false ? t('{label} cannot be deleted', { label: confirm.label })
              : t('Delete {label}', { label: confirm?.label ?? t('client') })
        }
        confirmLabel={
          confirm?.kind === 'archive-client' ? t('Archive')
            : confirm?.plan.allowed === false ? t('Archive instead')
              : busy ? t('Deleting…') : t('Delete permanently')
        }
        danger={confirm?.kind === 'delete-client' && confirm.plan.allowed}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runConfirm()}
      >
        {confirm?.kind === 'archive-client' && (
          <p className="text-sm text-ink-2">
            {t('Archive “{label}”? Engagements and history are preserved — the client simply drops out of the active list. Nothing is removed, and you can bring it back by setting the status to active.', { label: confirm.label })}
          </p>
        )}

        {confirm?.kind === 'delete-client' && !confirm.plan.allowed && (
          <>
            {confirm.plan.blockers.map((b) => (
              <p className="text-sm text-ink-2" key={b.code} style={{ lineHeight: 1.6 }}>{b.message}</p>
            ))}
          </>
        )}

        {confirm?.kind === 'delete-client' && confirm.plan.allowed && (
          <>
            <p className="text-sm text-ink-2">
              {t('Delete “{label}” outright? This is for a client that should never have existed — a real one that has finished gets archived instead.', { label: confirm.label })}
            </p>
            {confirm.plan.cascade.length > 0 ? (
              <ul className="text-sm text-ink-2 mt-8" style={{ paddingLeft: 18, lineHeight: 1.7 }}>
                {confirm.plan.cascade.map((c) => (
                  <li key={c.what}>{tx(t, '{count} {what} go with it', { count: <span className="voice-figure">{c.count}</span>, what: c.what })}</li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-ink-3 mt-8">{t('Nothing else hangs off this client.')}</p>
            )}
            {confirm.plan.engagements.length > 0 && (
              <p className="text-sm mt-8" style={{ color: 'var(--warn)', lineHeight: 1.6 }}>
                {tx(t, 'Including {codes} with every requirement and run inside them — and the client’s document library, stored files included.', {
                  codes: confirm.plan.engagements.map((e) => <span className="mono" key={e.id}>{e.code} </span>),
                })}
              </p>
            )}
            <p className="text-xs text-ink-3 mt-8">{t('Immediate and irreversible. The audit log keeps the record.')}</p>
          </>
        )}
      </ConfirmModal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row parts — every one of them renders a server string, never a re-worded one
// ---------------------------------------------------------------------------

/**
 * The identity line. A null short name renders NOTHING — an em-dash in the mono ref class is not
 * a checkable identity (ATTEST LAW 1), and `.n-none` exists for the absence.
 */
function Ident({ client }: { client: BoardClient }) {
  const t = useT();
  const place = [client.city, client.country].filter(Boolean).join(', ');
  const bits = [client.sector, place].filter(Boolean).join(' · ');
  return (
    <span className="cl-ident">
      {client.short_name && <span className="src-ref">{client.short_name}</span>}
      {bits && <span>{bits}</span>}
      {!client.short_name && !bits && <span className="n-none">{t('nothing on the client record')}</span>}
      {client.status !== 'active' && <span className="cl-statelabel">{t(client.status)}</span>}
    </span>
  );
}

/** Anything derived from an engagement_id renders inside that engagement's chip. Rule 2. */
function Engagements({ client }: { client: BoardClient }) {
  const t = useT();
  if (!client.engagements.length && !client.archived_engagements.length) return null;
  return (
    <div className="cl-engs">
      {client.engagements.map((e) => (
        <span className="cl-eng" key={e.id}>
          <span className="src-ref">{e.code}</span>
          <span className="ph">·</span>
          <span className="ph">{tPhase(t, crm.PHASE_LABELS[e.current_phase as Phase] ?? e.current_phase)}</span>
          <span className="ph">·</span>
          {e.gate_error ? (
            <span className="ph n-unknown" title={e.gate_error}>{t('{gate} tally unknown', { gate: e.gate })}</span>
          ) : e.gate_skipped ? (
            <span className="ph n-unknown" title={e.gate_skipped}>{e.gate} {e.gate_skipped}</span>
          ) : (
            <span className="ph">
              {tx(t, '{gate} {met} of {total} automatic', {
                gate: e.gate,
                met: <span className="voice-figure">{e.auto_met}</span>,
                total: <span className="voice-figure">{e.auto_total}</span>,
              })}
            </span>
          )}
        </span>
      ))}
      {client.archived_engagements.length > 0 && (
        <span className="cl-eng n-none">{t('+{n} archived', { n: client.archived_engagements.length })}</span>
      )}
    </div>
  );
}

/** Two figure slots on every row: fixed set, fixed order, '—' when there is nothing to state. */
function Figures({ client }: { client: BoardClient }) {
  const { t, fmtDate } = useLocale();
  const days = client.window.days_since;
  return (
    <div className="cl-figs">
      <div className="cl-fig">
        <div className="k">{t('Ball')}</div>
        {client.ball ? (
          <>
            <div className="v warn">{client.ball.owner === 'them' ? t('THEM') : t('US')}</div>
            <div className="d">
              {tx(t, '{ref} open {days} d', { ref: client.ball.ref, days: <span className="voice-figure">{client.ball.open_days}</span> })}
            </div>
          </>
        ) : (
          <><div className="v nil">—</div><div className="d">{t('no open commitment')}</div></>
        )}
      </div>
      <div className="cl-fig">
        <div className="k">{t('Last contact')}</div>
        {days === null ? (
          <><div className="v nil">—</div><div className="d">{t('never')}</div></>
        ) : (
          <>
            <div className={`v voice-figure${client.quiet ? ' warn' : ''}`}>{days}<span className="u">{t('d')}</span></div>
            <div className="d">
              {fmtDate(client.window.last_contact)}
              {client.last_ref ? <> · {client.last_ref}</> : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The move, as `buildQueue` wrote it. The title and the `why` are rendered UNEDITED (B7) — a
 * board that re-words the rule will one day disagree with the page it links to. The engagement
 * code is shown only when the client carries more than one live engagement (the scope rule).
 */
function Move({ client, move }: { client: BoardClient; move: BoardQueueItem }) {
  return (
    <div className="cl-move">
      <div className="cl-movetitle">
        {move.title}
        {move.engagement && client.engagements.length > 1 && (
          <> <span className="src-ref">{move.engagement.code}</span></>
        )}
      </div>
      <div className="cl-why">{move.why}</div>
    </div>
  );
}

/**
 * The lead card — the ONE loud element on the page. Krug p.109 asks for more than one visual
 * distinction: this gets a step up in scale AND a 2px ink edge on its own sheet.
 */
function LeadCard({
  client, primary, label, onMove, onOpen, onArchive, onDelete,
}: {
  client: BoardClient; primary: boolean; label: string; onMove: () => void; onOpen: () => void;
  onArchive: () => void; onDelete: () => void;
}) {
  const t = useT();
  const rest = client.queue.length - 1;
  return (
    <div className="sheet sheet--edge cl-lead" id={`client-row-${client.id}`}>
      <div className="cl-rowgrid">
        <div className="cl-rowmain">
          <div className="cl-idline"><span className="cl-name">{client.name}</span><Ident client={client} /></div>
          <Engagements client={client} />
          <Move client={client} move={client.queue[0]} />
          <div className="cl-verbs">
            <button type="button" className={`btn${primary ? ' btn-accent' : ''}`} onClick={onMove}>{label}</button>
            <button type="button" className="btn" onClick={onOpen}>{t('The brief →')}</button>
            {rest > 0 && (
              <span className="cl-more">
                {tx(t, rest === 1 ? '{n} more move on the brief' : '{n} more moves on the brief', {
                  n: <span className="voice-figure">{rest}</span>,
                })}
              </span>
            )}
            <RowDisposal client={client} onArchive={onArchive} onDelete={onDelete} />
          </div>
        </div>
        <Figures client={client} />
      </div>
    </div>
  );
}

/** A row is a navigation target; the move's own control sits inside it and stops the bubble. */
function ClickableRow({ id, onOpen, children }: { id: string; onOpen: () => void; children: React.ReactNode }) {
  return (
    <div
      className="cl-row"
      id={id}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      {children}
    </div>
  );
}

function MoveRow({
  client, label, onMove, onOpen, onArchive, onDelete,
}: {
  client: BoardClient; label: string; onMove: () => void; onOpen: () => void;
  onArchive: () => void; onDelete: () => void;
}) {
  const t = useT();
  const rest = client.queue.length - 1;
  return (
    <ClickableRow id={`client-row-${client.id}`} onOpen={onOpen}>
      <div className="cl-rowgrid">
        <div className="cl-rowmain">
          <div className="cl-idline"><span className="cl-name">{client.name}</span><Ident client={client} /></div>
          <Engagements client={client} />
          <Move client={client} move={client.queue[0]} />
          <div className="cl-verbs">
            <button
              type="button"
              className="btn btn-sm"
              onClick={(e) => { e.stopPropagation(); onMove(); }}
            >
              {label}
            </button>
            {rest > 0 && (
              <span className="cl-more">{tx(t, '{n} more on the brief', { n: <span className="voice-figure">{rest}</span> })}</span>
            )}
            <RowDisposal client={client} onArchive={onArchive} onDelete={onDelete} />
          </div>
        </div>
        <Figures client={client} />
      </div>
    </ClickableRow>
  );
}

function QuietRow({
  client, onOpen, onGate, onArchive, onDelete,
}: {
  client: BoardClient; onOpen: () => void; onGate: (engagementId: number) => void;
  onArchive: () => void; onDelete: () => void;
}) {
  const t = useT();
  const first = client.engagements[0];
  return (
    <ClickableRow id={`client-row-${client.id}`} onOpen={onOpen}>
      <div className="cl-rowgrid">
        <div className="cl-rowmain">
          <div className="cl-idline"><span className="cl-name">{client.name}</span><Ident client={client} /></div>
          <Engagements client={client} />
          {/* Composed server-side so two calls on unchanged data read identically. */}
          <div className="cl-why" style={{ marginTop: 'var(--s8)' }}>{client.quiet_sentence}</div>
          <div className="cl-verbs">
            {first && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={(e) => { e.stopPropagation(); onGate(first.id); }}
              >
                {t('Open the gate →')}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
            >
              {t('The brief →')}
            </button>
            <RowDisposal client={client} onArchive={onArchive} onDelete={onDelete} />
          </div>
        </div>
        <Figures client={client} />
      </div>
    </ClickableRow>
  );
}

/**
 * Every client row carries the same disposal affordance, in the same shape the
 * dashboard's engagement rows use: one quiet ⋯, never a danger button riding a
 * navigation target. Delete always asks the server for the PLAN first; a refusal
 * arrives with its reason and the dialogue offers the alternative it names.
 */
function RowDisposal({
  client, onArchive, onDelete,
}: { client: { id: number; name: string; status: string }; onArchive: () => void; onDelete: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginLeft: 'auto' }}>
      <RowMenu label={t('More for {name}', { name: client.name })} open={open} onOpenChange={setOpen}>
        {client.status !== 'archived' && (
          <button type="button" onClick={() => { setOpen(false); onArchive(); }}>
            {t('Archive — keeps the history')}
          </button>
        )}
        <button type="button" className="rowmenu-danger" onClick={() => { setOpen(false); onDelete(); }}>
          {t('Delete {name}…', { name: client.name })}
        </button>
      </RowMenu>
    </div>
  );
}

/**
 * A not-engaged row. It does NOT navigate on click — the disposals live here, and an icon 24px
 * from a full-row navigation target is the mis-click class this design removes.
 */
function DeadRow({
  client, onOpen, onArchive, onDelete,
}: { client: BoardClient; onOpen: () => void; onArchive: () => void; onDelete: () => void }) {
  const { t, tp, fmtDate } = useLocale();
  const what: string[] = [];
  if (client.counts.documents) what.push(tp(client.counts.documents, '{n} document', '{n} documents'));
  if (client.counts.contacts) what.push(tp(client.counts.contacts, '{n} contact', '{n} contacts'));
  if (client.archived_engagements.length) {
    what.push(tp(client.archived_engagements.length, '{n} archived engagement', '{n} archived engagements'));
  }
  const line = what.length
    ? t('{what} · last contact {date}', {
      what: what.join(' · '),
      date: client.window.last_contact ? fmtDate(client.window.last_contact) : t('never'),
    })
    : t('nothing on the record at all');
  return (
    <div className="cl-dead" id={`client-row-${client.id}`}>
      <div>
        <div className="cl-deadname">
          <span>{client.name}</span>
          {client.short_name && <span className="src-ref">{client.short_name}</span>}
          {client.status !== 'active' && <span className="cl-statelabel">{t(client.status)}</span>}
          {client.new_items > 0 && (
            <span className="cl-more"><span className="voice-figure">{client.new_items}</span> {t('new')}</span>
          )}
        </div>
        <div className="cl-deadwhat voice-figure">{line}</div>
      </div>
      <div className="cl-deadverbs">
        <button type="button" className="btn btn-sm" onClick={onOpen}>{t('Open →')}</button>
        {client.status !== 'archived' && (
          <button
            type="button"
            className="btn btn-sm"
            title={t('Archive — keeps every engagement, contact and document; the client leaves the active list')}
            onClick={onArchive}
          >
            {t('Archive')}
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm btn-danger"
          title={t('Delete {name} — for a row that should never have existed', { name: client.name })}
          onClick={onDelete}
        >
          {t('Delete')}
        </button>
      </div>
    </div>
  );
}

/** Loading preserves the rail's five cells and the zone rhythm — nothing reflows on arrival. */
function BoardSkeleton() {
  const t = useT();
  const bar = (w: string) => (
    <div className="cl-skel" style={{ width: w, marginBottom: 'var(--s8)' }} />
  );
  return (
    <section className="zone">
      <div className="zone-head">
        <span className="u-label">{t('Needs a move')}</span>
        <span className="zone-count">{t('reading the board…')}</span>
      </div>
      <div className="sheet sheet--edge cl-lead" style={{ borderLeftColor: 'var(--line-strong)' }}>
        {bar('38%')}{bar('62%')}{bar('88%')}{bar('54%')}
      </div>
      <div className="sheet sheet--clip cl-band">
        <div style={{ padding: '15px 18px' }}>{bar('30%')}{bar('70%')}</div>
      </div>
    </section>
  );
}
